/**
 * DropOutSuggestionCard — Renderer für Kontaktpausen-Hinweise.
 *
 * Compact card mit:
 *   - Tage seit letztem Kontakt
 *   - Datum des letzten Termins (falls bekannt)
 *   - "Patient öffnen" und Dismiss-Aktion
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isDropOutPayload,
  type AiSuggestion,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

export const DropOutSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isDropOutPayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}`);
    }
    await resolve(suggestion, 'accepted', 'open_patient');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('de-DE') : '—';

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium">Kontaktpause dokumentiert</span>
          <span className="text-xs text-muted-foreground">
            {p.days_since_last_contact} Tage inaktiv
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <p className="mb-2 text-sm text-foreground">{p.rationale}</p>

      <p className="mb-3 text-xs text-muted-foreground">
        Letzter Termin: {fmtDate(p.last_appointment_at)}
      </p>

      <Button size="sm" onClick={handlePrimary}>
        Patient öffnen
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </article>
  );
};
